import { useEffect, useState } from "react";
import { Container, Navbar, Nav, Badge, Button, Form } from "react-bootstrap";
import { CiHeart } from "react-icons/ci";
import { IoIosArrowForward } from "react-icons/io";
import { PiShoppingCartThin, PiUserLight } from "react-icons/pi";
import { TbHeartX, TbShoppingCartX } from "react-icons/tb";
import UseToggle from "../../../Hooks/UseToggle";
import { NavLink } from "react-router-dom";

const HeaderSection = ({
  headerSection,
  tabsManage = [],
  scrollToSection,
  themeColor,
  setActiveSection,
  activeSection,
  sendNotification
}) => {
  const toggles = UseToggle();  
  const [greeting, setGreeting] = useState("");
  const [showIcons, setShowIcons] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  useEffect(() => {
    // Show icons only when "store" is active
    setShowIcons(activeSection === "store");
  }, [activeSection]);

  useEffect(() => {
    const sections = tabsManage
      .filter((s) => s?.isShow)
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);

    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        root: null,
        rootMargin: "-120px 0px -60% 0px",
        threshold: 0,
      },
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [tabsManage]);

  const getGreeting = () => {
    const currentTime = new Date().getHours();
    let greeting;

    if (currentTime >= 5 && currentTime < 12) {
      greeting = "Good Morning";
    } else if (currentTime >= 12 && currentTime < 18) {
      greeting = "Good Afternoon";
    } else {
      greeting = "Good Evening";
    }
    setGreeting(greeting);
  };

  useEffect(() => {
    getGreeting();
  }, []);

  const handleNavClick = (menuId, menuName) => {
    setActiveSection(menuId);
    scrollToSection(menuId);
    sendNotification("notification", `Visitor Navigated to the ${menuName} Menu.`);
    setExpanded(false); // Close mobile menu after click
  };

  const handleLogoClick = () => {
    scrollToSection("home");
    setActiveSection("home");
    sendNotification("notification", "Visitor Navigated to the Homepage.");
    setExpanded(false);
  };

  return (
    <Navbar
      expand="lg"
      bg="white"
      className="shadow-sm sticky-top"
      style={{ zIndex: 1020 }}
      expanded={expanded}
      onToggle={setExpanded}
    >
      <Container>
        <div className="d-flex align-items-center w-100">
          {/* Logo and Brand */}
          <div className="d-flex align-items-center">
            <Navbar.Toggle 
              aria-controls="basic-navbar-nav" 
              className="me-2 order-0"
            />
            <div
              className="fw-bold fs-4 CP d-flex align-items-center"
              onClick={handleLogoClick}
            >
              <img
                src="https://tawk.link/5edc81e54a7c6258179a1405/var/logo/u30481uPlW.png?width=40&height=40"
                alt="logo"
                className="brand-logo me-2"
                style={{ cursor: "pointer", width: 40, height: 40 }}
              />
              <span>{headerSection?.pageName}</span>
            </div>
          </div>

          {/* Navigation Menu - Center when store is active, right when other menu is active */}
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className={`${showIcons ? 'mx-auto' : 'ms-auto'} gap-4`}>
              {tabsManage
                .filter((menu) => menu?.isShow)
                .map((menu) => (
                  <div
                    key={menu.id}
                    onClick={() => handleNavClick(menu.id, menu.name || menu.id)}
                    className="text-capitalize CP py-2"
                    style={{
                      color: activeSection === menu.id ? themeColor : undefined,
                      fontWeight: activeSection === menu.id ? 600 : 400,
                      cursor: "pointer"
                    }}
                  >
                    {menu.name || menu.id}
                  </div>
                ))}
            </Nav>
          </Navbar.Collapse>

          {/* Right Icons - Only visible when store is active and not in mobile menu */}
          {showIcons && (
            <div className="hstack justify-content-between py-2 gap-3 ms-auto d-none d-lg-flex">
              {/* Wishlist Popover */}
              <div className="popover__wrapper position-relative d-block">
                <div className="CP text-nowrap popover__title">
                  <CiHeart style={{ fontSize: 25 }} />
                  <Badge
                    className="position-absolute bg-color rounded-circle fs-11 hstack justify-content-center text-center"
                    style={{ top: -10, right: -10, width: 20, height: 20 }}
                  >
                    99
                  </Badge>
                </div>
                <div
                  className="popover__content position-absolute br3"
                  style={{ right: -140 }}
                >
                  <div className="p-3 text-center">
                    <TbHeartX style={{ width: 40, height: 40 }} className="text-muted" />
                    <p className="mt-3" style={{fontSize: 12}}>No products in the wishlist</p>
                  </div>
                  <div className="overflow-auto" style={{ maxHeight: 200 }}>
                    {[1,2,3].map((item) => (
                      <div key={item} className="hstack justify-content-between gap-3 border-bottom py-1 px-3">
                        <span
                          className="text-danger CP"
                          title="Remove from wishlist"
                        >
                          x
                        </span>
                        <img
                          src={`https://i.pravatar.cc/50?img=${item}`}
                          style={{ width: 50, height: 50, objectFit: "cover" }}
                          alt=""
                        />
                        <p className="m-0 fw-bold lh-1" style={{fontSize: 12}}>
                          Cotton Polo Regular Fit - Large
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="text-center w-100 p-3">
                    <Button size="sm" style={{fontSize: 10}} className="text-nowrap w-100" variant="outline-primary" onClick={toggles.setToggle1}>
                      View Wishlist <IoIosArrowForward/>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Cart Popover */}
              <div className="popover__wrapper position-relative d-block">
                <div className="CP text-nowrap popover__title">
                  <PiShoppingCartThin style={{ fontSize: 25 }} />
                  <Badge
                    className="position-absolute bg-color rounded-circle fs-11 hstack justify-content-center text-center"
                    style={{ top: -10, right: -10, width: 20, height: 20 }}
                  >
                    9
                  </Badge>
                </div>
                <div
                  className="popover__content position-absolute br3"
                  style={{ right: -140 }}
                >
                  <div className="p-3 text-center">
                    <TbShoppingCartX style={{ width: 40, height: 40 }} className="text-muted" />
                    <p className="mt-3" style={{fontSize: 12}}>No products in the cart</p>
                  </div>
                  <div className="overflow-auto" style={{ maxHeight: 200 }}>
                    {[1,2,3].map((item) => (
                      <div key={item} className="hstack justify-content-between gap-3 border-bottom py-2 px-3">
                        <span className="text-danger CP" title="Remove from Cart">
                          x
                        </span>
                        <img
                          src={`https://i.pravatar.cc/50?img=${item}`}
                          style={{ width: 50, height: 50, objectFit: "cover" }}
                          alt=""
                        />
                        <Form.Group>
                          <p className="m-0 fw-bold lh-1" style={{fontSize: 12}}>
                            Cotton Polo Regular Fit - Large
                          </p>
                          <p className="mt-2 m-0" style={{fontSize: 11}}>$129.00 x 1</p>
                        </Form.Group>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 border-top">
                    <p style={{fontSize: 12}}>
                      <b>Sub-total: </b> $129.00
                    </p>
                    <div className="hstack gap-2 w-100 justify-content-between">
                      <Button size="sm" style={{fontSize: 10}} className="text-nowrap w-100" variant="outline-primary" onClick={toggles.setToggle}>
                        Expand Cart <IoIosArrowForward />
                      </Button>
                      <NavLink to={`/cart`} className="text-dark ancherTag w-100">
                        <Button size="sm" style={{fontSize: 10}} className="text-nowrap w-100" variant="outline-primary">
                          View Cart <IoIosArrowForward/>
                        </Button>
                      </NavLink>
                      <NavLink to={`/checkout`} className="text-dark ancherTag w-100">
                        <Button size="sm" style={{fontSize: 10}} className="text-nowrap w-100" variant="outline-primary">
                          Checkout <IoIosArrowForward/>
                        </Button>
                      </NavLink>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Account */}
              <NavLink to={`/account`} className="text-dark ancherTag">
                <div className="position-relative CP text-nowrap">
                  <div className="hstack gap-2 lh-1">
                    <PiUserLight style={{ fontSize: 25 }} />
                    <div>
                      <p className="m-0" style={{fontSize: 10}}>{greeting},</p>
                      <p className="m-0" style={{fontSize: 12}}>Roop Chandra</p>
                    </div>
                  </div>
                </div>
              </NavLink>
            </div>
          )}

          {/* Mobile Icons - Always visible on mobile when store is active */}
          {showIcons && (
            <div className="d-lg-none ms-auto">
              <div className="hstack gap-2">
                <CiHeart style={{ fontSize: 22 }} />
                <PiShoppingCartThin style={{ fontSize: 22 }} />
                <PiUserLight style={{ fontSize: 22 }} />
              </div>
            </div>
          )}
        </div>
      </Container>
    </Navbar>
  );
};

export default HeaderSection;